// Pure, unit-tested transformation of an already-filtered task list into the
// chronological groups TaskTimeline.jsx renders. No fetching, no filtering
// (that stays Tasks.jsx's job): this module only groups and sorts. Reuses
// lib/tasks.js's parseLocalDate/startOfLocalDay, which avoid the
// shift-by-a-day bug of `new Date("YYYY-MM-DD")` (UTC-midnight parsing).
import { parseLocalDate, startOfLocalDay } from "./tasks";

// How many weeks around "today" are shown individually (not collapsed into a
// month): 1 before + the current week + 1 after (semaine passée / cette
// semaine / semaine suivante).
const NEAR_WEEKS_BEFORE = 1;
const NEAR_WEEKS_AFTER = 1;

const MONTH_LABELS_FR = [
  "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
];

function addDays(date, n) {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

// Monday-based week start (ISO 8601), same convention as lib/tasks.js's
// currentWeekBounds (not reused: that one returns a {start, end} pair, not a
// bucketing key).
function mondayOf(date) {
  const daysSinceMonday = (date.getDay() + 6) % 7; // Sun=0..Sat=6 -> Mon=0..Sun=6
  return addDays(date, -daysSinceMonday);
}

function isoDateKey(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function formatWeekLabel(monday) {
  const sunday = addDays(monday, 6);
  const sameMonth = monday.getMonth() === sunday.getMonth();
  const sameYear = monday.getFullYear() === sunday.getFullYear();
  const startMonth = MONTH_LABELS_FR[monday.getMonth()].toLowerCase();
  const endMonth = MONTH_LABELS_FR[sunday.getMonth()].toLowerCase();
  const startPart = sameMonth ? `${monday.getDate()}` : `${monday.getDate()} ${startMonth}`;
  const yearSuffix = sameYear ? "" : ` ${sunday.getFullYear()}`;
  return `${startPart} – ${sunday.getDate()} ${endMonth}${yearSuffix}`;
}

export function formatMonthLabel(year, month) {
  return `${MONTH_LABELS_FR[month]} ${year}`;
}

// Sorts ascending by due_date first: the one place this ordering happens;
// every grouping function below relies on it rather than re-sorting.
function splitAndSort(tasks) {
  const withDue = [];
  const noDate = [];
  for (const task of tasks) {
    const due = parseLocalDate(task.due_date);
    if (due) withDue.push({ task, due });
    else noDate.push(task);
  }
  withDue.sort((a, b) => a.due - b.due);
  return { withDue, noDate };
}

// Groups a set of {task, due} pairs into Monday-keyed weeks, each bucket
// sorted ascending by due date regardless of input order. Used both for the
// "near" window and for an expanded month's weekly sub-breakdown, so there is
// one week-grouping definition.
export function groupTasksByWeek(withDue) {
  const buckets = new Map();
  for (const entry of withDue) {
    const monday = mondayOf(entry.due);
    const key = isoDateKey(monday);
    if (!buckets.has(key)) buckets.set(key, { monday, entries: [] });
    buckets.get(key).entries.push(entry);
  }
  return [...buckets.values()]
    .sort((a, b) => a.monday - b.monday)
    .map((b) => ({
      monday: b.monday,
      tasks: [...b.entries].sort((a, c) => a.due - c.due).map((e) => e.task),
    }));
}

function groupTasksByMonth(withDue) {
  const buckets = new Map();
  for (const { task, due } of withDue) {
    const key = `${due.getFullYear()}-${String(due.getMonth() + 1).padStart(2, "0")}`;
    if (!buckets.has(key)) buckets.set(key, { year: due.getFullYear(), month: due.getMonth(), withDue: [] });
    buckets.get(key).withDue.push({ task, due });
  }
  return [...buckets.values()].sort((a, b) => (a.year - b.year) || (a.month - b.month));
}

// The main entry point: tasks (already filtered by the caller) -> an
// ordered list of sections (month / week / today_marker) plus the
// no-due-date bucket, kept separate since it renders as its own trailing
// section regardless of chronological position.
export function buildTimelineSections(tasks, now = new Date()) {
  const today = startOfLocalDay(now);
  const currentMonday = mondayOf(today);
  const nearStart = addDays(currentMonday, -7 * NEAR_WEEKS_BEFORE);
  const nearEnd = addDays(currentMonday, 7 * (NEAR_WEEKS_AFTER + 1)); // exclusive

  const { withDue, noDate } = splitAndSort(tasks);
  const near = [];
  const far = [];
  for (const entry of withDue) {
    const monday = mondayOf(entry.due);
    if (monday >= nearStart && monday < nearEnd) near.push(entry);
    else far.push(entry);
  }

  const weeks = groupTasksByWeek(near).map((w) => ({
    type: "week",
    key: `week-${isoDateKey(w.monday)}`,
    monday: w.monday,
    isCurrentWeek: w.monday.getTime() === currentMonday.getTime(),
    isPastWeek: w.monday < currentMonday,
    tasks: w.tasks,
  }));

  const pastMonths = groupTasksByMonth(far.filter((e) => new Date(e.due.getFullYear(), e.due.getMonth(), 1) < currentMonday));
  const futureMonths = groupTasksByMonth(far.filter((e) => new Date(e.due.getFullYear(), e.due.getMonth(), 1) >= currentMonday));

  const toMonthSection = (m) => ({
    type: "month", key: `month-${m.year}-${m.month}`, year: m.year, month: m.month,
    count: m.withDue.length, withDue: m.withDue,
  });

  const sections = [...pastMonths.map(toMonthSection)];
  let todayInserted = false;
  for (const week of weeks) {
    if (!todayInserted && week.monday.getTime() >= currentMonday.getTime()) {
      sections.push({ type: "today_marker", key: "today-marker", date: today });
      todayInserted = true;
    }
    sections.push(week);
  }
  if (!todayInserted) {
    sections.push({ type: "today_marker", key: "today-marker", date: today });
  }
  sections.push(...futureMonths.map(toMonthSection));

  return { sections, noDateTasks: noDate };
}
